import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, ChevronDown, Check, Plus, ArrowLeft, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import type { AppRole } from '@/src/lib/types';
import DeleteVenueConfirm from './delete-venue-confirm';

interface VenueOption {
  id: string;
  name: string;
  role: string;
  active: boolean;
}

interface VenueSwitcherProps {
  venueName?: string;
  userRole: AppRole;
}

export default function VenueSwitcher({ venueName, userRole }: VenueSwitcherProps) {
  const c = useAppColors();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [venues, setVenues] = useState<VenueOption[] | null>(null);
  const [switching, setSwitching] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Anyone can start a new venue; they become its owner.
  const canCreate = true;
  const activeVenueRole = venues?.find((v) => v.active)?.role;
  const canDeleteActiveVenue = activeVenueRole === 'owner';

  useEffect(() => {
    let cancelled = false;
    supabase.functions.invoke('venues', { body: { action: 'list' } }).then(({ data, error }) => {
      if (cancelled || error || !data?.ok) return;
      setVenues(data.venues as VenueOption[]);
    });
    return () => { cancelled = true; };
  }, []);

  const openDropdown = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.left, width: rect.width });
    }
    setMode('list');
    setCreateError(null);
    setOpen((o) => !o);
  };

  // Single-venue, non-owner: a plain non-interactive chip, nothing to do here.
  if ((!venues || venues.length <= 1) && !canCreate) {
    return (
      <div
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
        style={{ background: c.iconBg, color: c.textFaint }}
      >
        <Building2 size={14} className="flex-shrink-0" />
        <span className="flex-1 text-left truncate font-medium" style={{ color: c.text }}>{venueName}</span>
      </div>
    );
  }

  const handleSelect = async (target: VenueOption) => {
    if (target.active || switching) { setOpen(false); return; }
    setSwitching(true);
    const { data, error } = await supabase.functions.invoke('venues', {
      body: { action: 'switch', venue_id: target.id },
    });
    if (error || !data?.ok) {
      setSwitching(false);
      setOpen(false);
      return;
    }
    window.location.reload();
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setCreateError(null);
    const { data, error } = await supabase.functions.invoke('venues', {
      body: { action: 'create', name },
    });
    if (error || !data?.ok) {
      setCreating(false);
      setCreateError((data as { error?: string })?.error ?? 'Could not create venue.');
      return;
    }
    // create already makes the new venue active server-side — same "start fresh" reload as switch.
    window.location.reload();
  };

  const activeVenueId = venues?.find((v) => v.active)?.id ?? '';

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors"
        style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = c.accentBorder; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = c.border; }}
      >
        <Building2 size={14} className="flex-shrink-0" style={{ color: c.textFaint }} />
        <span className="flex-1 text-left truncate font-medium">{venueName}</span>
        <ChevronDown
          size={13}
          className="flex-shrink-0 transition-transform"
          style={{ color: c.textFaint, transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>

      {open && coords && createPortal(
        <>
          <div className="fixed inset-0 z-[999]" onClick={() => setOpen(false)} />
          <AnimatePresence mode="wait">
            {mode === 'list' ? (
              <motion.div
                key="list"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.12 }}
                className="fixed rounded-xl overflow-hidden z-[1000]"
                style={{
                  bottom: window.innerHeight - coords.top + 8,
                  left: coords.left,
                  width: coords.width,
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.24)',
                }}
              >
                {venues && venues.length > 1 && (
                  <>
                    <div
                      className="px-3 pt-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: c.textFaint }}
                    >
                      Switch venue
                    </div>
                    {venues.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => handleSelect(v)}
                        disabled={switching}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left transition-colors disabled:opacity-50"
                        style={{ color: v.active ? c.accentSoft : c.text }}
                        onMouseEnter={(e) => { if (!v.active) (e.currentTarget as HTMLButtonElement).style.background = c.iconBg; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                      >
                        <span className="flex flex-col overflow-hidden">
                          <span className="truncate font-medium">{v.name}</span>
                          <span className="text-[11px] capitalize" style={{ color: c.textFaint }}>{v.role}</span>
                        </span>
                        {v.active && <Check size={14} className="flex-shrink-0" />}
                      </button>
                    ))}

                    {canDeleteActiveVenue && venues && (
                      <button
                        onClick={() => { setOpen(false); setShowDeleteConfirm(true); }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors"
                        style={{ color: '#E8746A', borderTop: `1px solid ${c.border}` }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(232,116,106,0.08)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                      >
                        <Trash2 size={14} className="flex-shrink-0" />
                        Delete this venue
                      </button>
                    )}
                  </>
                )}

                {canCreate && (
                  <button
                    onClick={() => { setMode('create'); setNewName(''); setCreateError(null); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors"
                    style={{
                      color: c.accentSoft,
                      borderTop: venues && venues.length > 1 ? `1px solid ${c.border}` : undefined,
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = c.iconBg; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  >
                    <Plus size={14} className="flex-shrink-0" />
                    Add venue
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="create"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.12 }}
                className="fixed rounded-xl overflow-hidden z-[1000] p-3"
                style={{
                  bottom: window.innerHeight - coords.top + 8,
                  left: coords.left,
                  width: Math.max(coords.width, 220),
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.24)',
                }}
              >
                <button
                  onClick={() => setMode('list')}
                  className="flex items-center gap-1.5 text-[11px] mb-2"
                  style={{ color: c.textFaint }}
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: c.textFaint }}>
                  New venue name
                </label>
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                  placeholder="e.g. The Bistro"
                  className="w-full px-2.5 py-2 rounded-lg text-sm mb-2 outline-none"
                  style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
                />
                {createError && (
                  <p className="text-[11px] mb-2" style={{ color: '#E8746A' }}>{createError}</p>
                )}
                <button
                  onClick={handleCreate}
                  disabled={!newName.trim() || creating}
                  className="w-full py-2 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-50"
                  style={{ background: c.accentBg, color: c.accentSoft, border: `1px solid ${c.accentBorder}` }}
                >
                  {creating ? 'Creating…' : 'Create venue'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </>,
        document.body
      )}

      {showDeleteConfirm && venueName && (
        <DeleteVenueConfirm
          venueId={activeVenueId}
          venueName={venueName}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}